import { createSlice, current } from "@reduxjs/toolkit";
import { type } from "@testing-library/user-event/dist/type";
import { api, apiErrorsHandler } from "../../api/httpclient";
import {
  setCounter,
  decrementCounter,
} from "../../redux/slices/notificationscounterslice";
import { NOTIFICATIONS_PAGE_SIZE } from "../../settings";
import i18next from "i18next";
 

export const RAW_DATA_PAGE_SIZE = 250;
const defaultType = { Id: 0, Title: i18next.t("notifications.allnot") };
const types = [
  { ...defaultType },
  { Id: 1, Title: i18next.t("header.alerts") },
  { Id: 2, Title: i18next.t("notifications.messages")  },
  { Id: 3, Title: i18next.t("header.data") },
  { Id: 4, Title: i18next.t("header.datacorrection") },
];
const pageSize = NOTIFICATIONS_PAGE_SIZE; // 25;

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  currentType: defaultType,
  currentTitle: "",
  currentImportance: 0,
  formValues: {},
  items: [],
  currentPageIndex: 0,
  totalCount: 0,
  pagesCount: 0,
  lookups: {},
  panes: [
    {
      // size: "30%",
      size: "25%",
      min: "22%",
      max: "30%",
      collapsible: false,
    },
    {
      //min: "700px",
      //size:"60%",
      min: "70%",
      max: "78%",
      collapsible: false,
    },
  ],
  toolsVisible: true,
  groupedByImportanceItems:[],
  groupedByTypeItems:[]
};

const getRawDataGroupings = (groupedData) => {
  const headers = groupedData.Headers;
  const values = groupedData.Values;

  const result = [{ Id: 0, Title: "None" }];

  for (let v in values) {
    let value = values[v];
    let title = "";

    for (let h in headers) {
      const header = headers[h];
      if (
        header != "AchievedValue" &&
        header != "ThresholdMin" &&
        header != "ThresholdMax" &&
        header != "OperatorTitle" &&
        header != "KeyMetric" &&
        header != "Variation"
      )
        title += value[header] + " - ";
    }

    result.push({
      Id: value.AlertLogId,
      Title: title.substring(0, title.length - 2),
    });
  }

  return result;
};

const notificationsPageSlice = createSlice({
  name: "notificationsPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }),

    setProcessing: (state, action) => {
      //payload: {processing=true, processingText=false}, when processing is false, processingText is ""
      const { processing = true, processingText = "" } = action.payload;

      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),

    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }),

    setFormValue: (state, action) => {
      //payload: {id: fieldId, value: fieldValue}
      let id = action.payload.id;
      let value = action.payload.value;

      return {
        ...state,
        formValues: { ...state.formValues, [id]: value },
      };
    },

    pageLoaded: (state, action) => {
      //payload: {Items: [{},  ], TotalRowsCount, TotalPagesCount }
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      let currentPageIndex = action.payload.TotalPagesCount == 0 ? -1 : 0;

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        currentType: defaultType,
        currentTitle: "",
        currentImportance: 0,
        items: action.payload.Items,
        currentPageIndex: currentPageIndex,
        totalCount: action.payload.TotalRowsCount,
        pagesCount: action.payload.TotalPagesCount,
        formValues: {},
        groupedByImportanceItems: action.payload.GroupedByImportanceItems,
        groupedByTypeItems: action.payload.GroupedByTypeItems,
      };
    },

    entityLoaded: (state, action) => {
      //action.payload: the fetched entity
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      let item = state.items.find((e) => e.Id == action.payload.Id);

      let entity = { ...action.payload, tabIndex: 0 };
      if (item && item.Status == -1)
        return {
          ...state,
          processing: false,
          processingText: "",
          items: state.items.map((item) => {
            if (item.Id == action.payload.Id) return { ...item, Status: 1 };
            else return item;
          }),
          formValues: {
            ...entity,
            rawDataFields: entity.Fields.filter(
              (e) => e.IsAggregate == false
            ).map((item) => ({ ...item, Checked: false, IsVisible: true })),
            rawDataFieldGroups: entity.Fields.map((e) => e.FieldGroupTitle),
            rawDataAlertLogId: 0,
            rawDataGroupings: entity.IsGrouped
              ? getRawDataGroupings(entity.GroupedData)
              : [],
            rawDataGroupingId: 0,
            rawDataDirection: 1,
            rawDataRowsCount: 500,
            rawDataHeaders: [],
            rawDataValues: [],
            rawDataDialogOpen: false,
            rawDataGridSort: [],
            rawDataGridPageSkip: 0,
            rawDataGridFilter: {},
            rawDataGridData: [],
            rawDataGridDataPaged: [],
          },
        };
      else
        return {
          ...state,
          processing: false,
          processingText: "",
          formValues: {
            ...entity,
            rawDataFields: entity.Fields.filter(
              (e) => e.IsAggregate == false
            ).map((item) => ({ ...item, Checked: false, IsVisible: true })),
            rawDataFieldGroups: entity.Fields.map((e) => e.FieldGroupTitle),
            rawDataAlertLogId: 0,
            rawDataGroupings: entity.IsGrouped
              ? getRawDataGroupings(entity.GroupedData)
              : [],
            rawDataGroupingId: 0,
            rawDataDirection: 1,
            rawDataRowsCount: 500,
            rawDataHeaders: [],
            rawDataValues: [],
            rawDataDialogOpen: false,
            rawDataGridSort: [],
            rawDataGridPageSkip: 0,
            rawDataGridFilter: [],
            rawDataGridData: [],
            rawDataGridDataPaged: [],
          },
        };
    },

    pageFiltered: (state, action) => {
      //payload: data: { Items: [{},  ], TotalRowsCount, TotalPagesCount, IsHit }, typeId, title
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      let currentPageIndex = action.payload.data.TotalPagesCount == 0 ? -1 : 0;

      const data = action.payload.data;
      const typeId = action.payload.typeId;
      const title = action.payload.title;
      const importance = action.payload.importance;

      return {
        ...state,
        processing: false,
        processingText: "",
        currentType: types.find((e) => e.Id == typeId),
        currentTitle: title,
        currentImportance: importance,
        items: data.Items,
        currentPageIndex: currentPageIndex,
        totalCount: data.TotalRowsCount,
        pagesCount: data.TotalPagesCount,
        formValues: {},
      };
    },

    movedFirst: (state, action) => {
      //payload: {Items: [{},  ], TotalRowsCount, TotalPagesCount }
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      let currentPageIndex = action.payload.TotalRowsCount == 0 ? -1 : 0;

      return {
        ...state,
        processing: false,
        processingText: "",
        items: action.payload.Items,
        currentPageIndex: currentPageIndex,
        totalCount: action.payload.TotalRowsCount,
        pagesCount: action.payload.TotalPagesCount,
        formValues: {},
      };
    },

    movedPrevious: (state, action) => {
      //payload: {Items: [{},  ], TotalRowsCount, TotalPagesCount }
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      let currentPageIndex =
        action.payload.TotalPagesCount == 0
          ? -1
          : action.payload.IsHit
          ? pageState.currentPageIndex - 1
          : 0; // (action.payload.TotalPagesCount - 1));

      return {
        ...state,
        processing: false,
        processingText: "",
        items: action.payload.Items,
        currentPageIndex: currentPageIndex,
        totalCount: action.payload.TotalRowsCount,
        pagesCount: action.payload.TotalPagesCount,
        formValues: {},
      };
    },

    movedNext: (state, action) => {
      //payload: {Items: [{},  ], TotalRowsCount, TotalPagesCount }
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      let currentPageIndex =
        action.payload.TotalPagesCount == 0
          ? -1
          : action.payload.IsHit
          ? pageState.currentPageIndex + 1
          : action.payload.TotalPagesCount - 1;

      return {
        ...state,
        processing: false,
        processingText: "",
        items: action.payload.Items,
        currentPageIndex: currentPageIndex,
        totalCount: action.payload.TotalRowsCount,
        pagesCount: action.payload.TotalPagesCount,
        formValues: {},
      };
    },

    movedLast: (state, action) => {
      //payload: {Items: [{},  ], TotalRowsCount, TotalPagesCount }
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      let currentPageIndex =
        action.payload.TotalPagesCount == 0
          ? -1
          : action.payload.TotalPagesCount - 1;

      return {
        ...state,
        processing: false,
        processingText: "",
        items: action.payload.Items,
        currentPageIndex: currentPageIndex,
        totalCount: action.payload.TotalRowsCount,
        pagesCount: action.payload.TotalPagesCount,
        formValues: {},
      };
    },

    markedAsRead: (state, action) => {
      //payload: id
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const id = action.payload;

      return {
        ...state,
        processing: false,
        processingText: "",
        items: state.items.map((item) => {
          if (item.Id == id) return { ...item, Status: 1 };
          else return item;
        }),
        formValues:
          pageState.formValues.Id == id
            ? { ...pageState.formValues, Status: 1 }
            : pageState.formValues,
      };
    },

    markedAsUnread: (state, action) => {
      //payload: id
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const id = action.payload;

      return {
        ...state,
        processing: false,
        processingText: "",
        items: state.items.map((item) => {
          if (item.Id == id) return { ...item, Status: -1 };
          else return item;
        }),
        formValues:
          pageState.formValues.Id == id
            ? { ...pageState.formValues, Status: -1 }
            : pageState.formValues,
      };
    },

    notificationAdded: (state, action) => {
      //payload: {Id, Title, Type. Importantce, Status, InitiatedOn, InitiatedBy(string), AlertId, AlertSessionDetailId, IsInitiator, Users:[string: guid]}
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const notification = action.payload;
      let title = pageState.currentTitle;

      //check if type applies
      if (
        (pageState.currentType.Id == 0 ||
          pageState.currentType.Id == action.payload.Type) &&
        (pageState.currentImportance == 0 ||
          pageState.currentImportance == action.payload.Importance)
      ) {
        let newTotalCount = pageState.totalCount + 1;

        if (title && title.trim().length > 0) {
          //add it to items only if filter applies
          title = title.toLowerCase();

          if (
            notification.Title.toLowerCase().indexOf(title) >= 0 ||
            notification.initiatedBy.toLowerCase().indexOf(title) >= 0
          )
            return {
              ...state,
              totalCount: newTotalCount,
              items: [notification, ...state.items],
            };
          //filtered and filter does not apply => do NOT add
          else return state;
        } //add it for sure
        else {
          return {
            ...state,
            totalCount: newTotalCount,
            items: [notification, ...state.items],
          };
        }
      } //do not add
      else return state;
    },

    notificationRemoved: (state, action) => {
      //payload: id
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const id = action.payload;

      return {
        ...state,
        processing: false,
        processingText: "",
        items: state.items.filter((e) => e.Id != id),
        formValues: pageState.formValues.Id == id ? {} : pageState.formValues,
      };
    },

    notificationsRemoved: (state, action) => {
      //payload: id
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      return {
        ...state,
        processing: false,
        processingText: "",
        //items: state.items,
      };
    },

    alertRemoved: (state, action) => {
      //payload: id = alert id
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const alertId = action.payload;

      return {
        ...state,
        items: state.items.filter((e) => e.AlertId != alertId),
        formValues:
          pageState.formValues.AlertId == alertId ? {} : pageState.formValues,
      };
    },

    removeEntityFromUI: (state, action) => {
      //payload: id
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      //remove from items
      let id = action.payload;

      if (pageState.items.find((e) => e.Id == id)) {
        if (pageState.formValues.Id == id)
          return {
            ...state,
            formValues: {},
            items: state.items.filter((e) => e.Id != id),
          };
        else return { ...state, items: state.items.filter((e) => e.Id != id) };
      } else return state;
    },

    toggleRawDataField: (state, action) => {
      //payload: id
      const pageState = current(state);

      const id = action.payload;

      return {
        ...state,
        formValues: {
          ...state.formValues,
          rawDataFields: state.formValues.rawDataFields.map((e) => {
            if (e.Id == id) return { ...e, Checked: !e.Checked };
            else return e;
          }),
        },
      };
    },

    rawDataLoaded: (state, action) => {
      const pageState = current(state);
      if (pageState.pageDisposed) return state;

      const data = action.payload;

      const headers = data.Headers;
      const table = JSON.parse(data.Table);

      return {
        ...state,
        processing: false,
        processingText: "",
        formValues: {
          ...state.formValues,
          rawDataHeaders: headers,
          rawDataValues: table,
          rawDataDialogOpen: false,
          rawDataGridSort: [],
          rawDataGridPageSkip: 0,
          rawDataGridFilter: {},
          rawDataGridData: table,
          rawDataGridDataPaged: table.slice(0, RAW_DATA_PAGE_SIZE),
        },
      };
    },

    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },
  },
});

export default notificationsPageSlice.reducer;

export const loadPage = (t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingPage") })
  );

  try {
    var response = await api({
      url: "notification/getallnotifications",
      method: "post",
      data: {
        type: 0,
        title: "",
        importance: 0,
        pageIndex: 0,
        pageSize: pageSize,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().notificationsPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response

    //update unread notifications count
    dispatch(setCounter(data.TotalUnreadCount));

    //proceed
    dispatch(pageLoaded(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadEntity = (id, t) => async (dispatch, getState) => {
  const pageState = getState().notificationsPage;
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingNotif"),
    })
  );

  try {
    var response = await api({
      url: "notification/getnotificationbyid",
      method: "post",
      data: {
        id: id,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().notificationsPage.pageDisposed) return;

    //it's ok to dispatch here
    dispatch(entityLoaded(response.data.Data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const filterPage =
  (typeId, title, importance, t) => async (dispatch, getState) => {
    //show loader
    dispatch(
      setProcessing({
        processing: true,
        processingText: t("slices.fetchingNotif"),
      })
    );

    const pageState = getState().notificationsPage;

    try {
      var response = await api({
        url: "notification/getallnotifications",
        method: "post",
        data: {
          type: typeId,
          title: title,
          importance: importance,
          pageIndex: 0,
          pageSize: pageSize,
        },
        //headers:{'h':'1'}
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().notificationsPage.pageDisposed) return;

      //it's ok to dispatch here
      let data = response.data.Data;

      //transform the response

      //update unread notifications count
      dispatch(setCounter(data.TotalUnreadCount));

      //proceed
      dispatch(pageFiltered({ data, typeId, title, importance }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const moveFirst = (t) => async (dispatch, getState) => {
  const pageState = getState().notificationsPage;

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.fetchingNotif"),
    })
  );

  try {
    var response = await api({
      url: "notification/getallnotifications",
      method: "post",
      data: {
        type: pageState.currentType.Id,
        title: pageState.currentTitle,
        importance: pageState.currentImportance,
        pageIndex: 0,
        pageSize: pageSize,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().notificationsPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response

    //update unread notifications count
    dispatch(setCounter(data.TotalUnreadCount));

    //proceed
    //NEED EXTRA CODE TO CHECK IF NO MORE DATA

    dispatch(movedFirst(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const movePrevious = (t) => async (dispatch, getState) => {
  const pageState = getState().notificationsPage;

  if (pageState.currentPageIndex <= 0) return Promise.resolve();

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.fetchingNotif"),
    })
  );

  try {
    var response = await api({
      url: "notification/getallnotifications",
      method: "post",
      data: {
        type: pageState.currentType.Id,
        title: pageState.currentTitle,
        importance: pageState.currentImportance,
        pageIndex: pageState.currentPageIndex - 1,
        pageSize: pageSize,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().notificationsPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response

    //update unread notifications count
    dispatch(setCounter(data.TotalUnreadCount));

    //proceed
    //NEED EXTRA CODE TO CHECK IF NO MORE DATA

    dispatch(movedPrevious(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const moveNext = (t) => async (dispatch, getState) => {
  const pageState = getState().notificationsPage;

  if (pageState.currentPageIndex == pageState.pagesCount - 1)
    return Promise.resolve();

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.fetchingNotif"),
    })
  );

  try {
    var response = await api({
      url: "notification/getallnotifications",
      method: "post",
      data: {
        type: pageState.currentType.Id,
        title: pageState.currentTitle,
        importance: pageState.currentImportance,
        pageIndex: pageState.currentPageIndex + 1,
        pageSize: pageSize,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().notificationsPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response

    //update unread notifications count
    dispatch(setCounter(data.TotalUnreadCount));

    //proceed
    //NEED EXTRA CODE TO CHECK IF NO MORE DATA

    dispatch(movedNext(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const moveLast = (t) => async (dispatch, getState) => {
  const pageState = getState().notificationsPage;

  if (pageState.currentPageIndex == pageState.pagesCount - 1)
    return Promise.resolve();

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.fetchingNotif"),
    })
  );

  try {
    var response = await api({
      url: "notification/getallnotifications",
      method: "post",
      data: {
        type: pageState.currentType.Id,
        title: pageState.currentTitle,
        importance: pageState.currentImportance,
        pageIndex: pageState.pagesCount - 1,
        pageSize: pageSize,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().notificationsPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response

    //update unread notifications count
    dispatch(setCounter(data.TotalUnreadCount));

    //proceed
    //NEED EXTRA CODE TO CHECK IF NO MORE DATA

    dispatch(movedLast(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const markAsRead = (id, t) => async (dispatch, getState) => {
  const pageState = getState().notificationsPage;
  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.applyingChanges") })
  );

  try {
    var response = await api({
      url: "notification/setnotificationStatus",
      method: "post",
      data: {
        id: id,
        status: 1,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().notificationsPage.pageDisposed) return;

    //it's ok to dispatch here

    dispatch(markedAsRead(id));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const markAsUnread = (id, t) => async (dispatch, getState) => {
  const pageState = getState().notificationsPage;
  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.applyingChanges") })
  );

  try {
    var response = await api({
      url: "notification/setnotificationStatus",
      method: "post",
      data: {
        id: id,
        status: -1,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().notificationsPage.pageDisposed) return;

    //it's ok to dispatch here

    dispatch(markedAsUnread(id));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const removeUserNotification = (id, t) => async (dispatch, getState) => {
  const pageState = getState().notificationsPage;
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.removingNotif"),
    })
  );

  try {
    var response = await api({
      url: "notification/removenotification",
      method: "post",
      data: {
        id: id,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().notificationsPage.pageDisposed) return;

    //it's ok to dispatch here
    dispatch(notificationRemoved(id));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const removeUserNotifications =
  (type, title, importance, t) => async (dispatch, getState) => {
    const pageState = getState().notificationsPage;
    //show loader
    dispatch(
      setProcessing({
        processing: true,
        processingText: t("slices.removingNotifs"),
      })
    );

    try {
      var response = await api({
        url: "notification/removeusernotifications",
        method: "post",
        data: {
          type: type,
          title: title,
          importance: importance,
        },
        //headers:{'h':'1'}
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().notificationsPage.pageDisposed) return;

      //it's ok to dispatch here
      dispatch(notificationsRemoved());

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const loadRawData = (t) => async (dispatch, getState) => {
  const pageState = getState().notificationsPage;
  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingRawData") })
  );

  try {
    var response = await api({
      url: "alert/getalertrawdata",
      method: "post",
      data: {
        sessionDetailId: pageState.formValues.AlertSessionDetailId,
        logId:
          pageState.formValues.rawDataAlertLogId == 0
            ? null
            : pageState.formValues.rawDataAlertLogId,
        fieldsIds: pageState.formValues.rawDataFields
          .filter((e) => e.Checked == true)
          .map((item) => item.Id),
        direction: pageState.formValues.rawDataDirection,
        rowsCount: pageState.formValues.rawDataRowsCount,
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().notificationsPage.pageDisposed) return;

    //it's ok to dispatch here
    dispatch(rawDataLoaded(response.data.Data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  pageLoaded,
  pageFiltered,
  alertRemoved,
  notificationAdded,
  movedFirst,
  movedPrevious,
  movedNext,
  movedLast,
  markedAsRead,
  markedAsUnread,
  notificationRemoved,
  notificationsRemoved,
  removeEntityFromUI,
  setPanes,
  dataLoaded,
  setFormValue,
  entityLoaded,
  toggleRawDataField,
  rawDataLoaded,
  disposePage,
} = notificationsPageSlice.actions;
